<div
  class="g-filter-scope flex flex-col gap-1.5"
  [class.grp]="placement() !== 'association'"
  [class.is-nested]="placement() === 'nested'"
  [class.g-negated-rail]="placement() !== 'association' && group().negated"
  [class.is-linked]="placement() !== 'association' && hovered() === group().id"
  [attr.data-path-node]="placement() !== 'association' ? group().id : null">
  <!-- A block's body shows its mode in the block's own header. -->
  @if (placement() !== 'association') {
    <div class="g-filter-row items-center">
      <div class="grp-h col-span-3 min-w-0 flex-wrap">
        <div
          class="gx-segmented is-sm"
          role="radiogroup"
          [attr.aria-label]="
            'features.data.path_filter.group.of_these_true.' + mode() | translate
          ">
          @for (option of modes; track option) {
            <button
              type="button"
              role="radio"
              class="seg"
              [class.is-active]="mode() === option"
              [class.is-negation]="option === 'none'"
              [attr.aria-checked]="mode() === option"
              (click)="setMode(option)">
              {{ 'features.data.path_filter.group.mode.' + option | translate }}
            </button>
          }
        </div>
        <span>{{
          'features.data.path_filter.group.of_these_true.' + mode() | translate
        }}</span>
      </div>
      @if (placement() === 'nested') {
        <button
          type="button"
          class="text-text-tertiary hover:text-danger hover:bg-danger-soft inline-flex cursor-pointer rounded-xs p-1.5 transition-colors"
          [attr.aria-label]="'features.data.path_filter.group.remove' | translate"
          (click)="removed.emit()">
          <i [class]="ICONS.trash" aria-hidden="true"></i>
        </button>
      }
    </div>
  }

  @for (child of group().children; track child.id) {
    @switch (child.kind) {
      @case ('condition') {
        <app-path-filter-condition
          [condition]="child"
          [edge]="edge()"
          [context]="context()"
          (conditionChange)="replaceChild($index, $event)"
          (associationPicked)="
            replaceWithAssociation($index, $event.edge, $event.quantifier)
          "
          (removed)="removeChild($index)" />
      }
      @case ('association') {
        <app-path-filter-association
          [association]="child"
          [scope]="edge()"
          [context]="context()"
          (associationChange)="replaceChild($index, $event)"
          (removed)="removeChild($index)">
          <app-path-filter-group
            placement="association"
            [addLabel]="addLabels().get(child.id) ?? null"
            [group]="child.filter"
            [edge]="scopeOf(child)"
            [context]="context()"
            (groupChange)="replaceAssociationFilter($index, child, $event)" />
        </app-path-filter-association>
      }
      @case ('group') {
        <app-path-filter-group
          [group]="child"
          [edge]="edge()"
          [context]="context()"
          (groupChange)="replaceChild($index, $event)"
          (removed)="removeChild($index)" />
      }
      @case ('opaque') {
        <div
          class="flex items-center gap-1.5 rounded-xs"
          [class.is-linked]="hovered() === child.id"
          [attr.data-path-node]="child.id">
          <span
            class="border-weak bg-page min-w-0 flex-1 rounded-xs border px-2 py-1 font-mono text-[12.5px]"
            [title]="'features.data.path_filter.condition.opaque' | translate">
            <app-dsl-text [text]="opaqueText(child)" />
          </span>
          <button
            type="button"
            class="text-text-tertiary hover:text-danger hover:bg-danger-soft inline-flex cursor-pointer rounded-xs p-1.5 transition-colors"
            [attr.aria-label]="'features.data.path_filter.condition.remove' | translate"
            (click)="removeChild($index)">
            <i [class]="ICONS.trash" aria-hidden="true"></i>
          </button>
        </div>
      }
    }
  }

  <div>
    <button
      #addButton
      data-testid="path-filter-add"
      type="button"
      class="text-accent-main hover:bg-accent-interactive inline-flex cursor-pointer items-center gap-1.5 rounded-xs px-2 py-1 text-xs font-semibold transition-colors"
      [class.bg-accent-interactive]="addMenuOpen()"
      [attr.aria-expanded]="addMenuOpen()"
      (click)="addMenuOpen.set(!addMenuOpen())">
      <i [class]="ICONS.plus" aria-hidden="true"></i>{{ addText() }}
    </button>
    <app-dropdown
      [target]="addButton"
      [template]="addMenu"
      [isOpen]="addMenuOpen()"
      [onClose]="closeAddMenu"
      [useDefaultStyles]="false" />
  </div>
</div>

<ng-template #addMenu>
  <div class="g-menu" role="menu" appFocusFirst (keydown)="onAddMenuKey($event)">
    <button
      type="button"
      role="menuitem"
      class="g-menu-item w-full"
      (click)="addCondition()">
      <i [class]="ICONS.funnelSimple" aria-hidden="true"></i>
      <span class="flex-1 text-left">{{
        'features.data.path_filter.add.condition' | translate
      }}</span>
    </button>
    <button type="button" role="menuitem" class="g-menu-item w-full" (click)="addGroup()">
      <i [class]="ICONS.bracketsRound" aria-hidden="true"></i>
      <span class="flex-1 text-left">{{
        'features.data.path_filter.add.group' | translate
      }}</span>
    </button>
    @if (associations().length > 0) {
      <div class="g-menu-divider"></div>
      <div class="g-menu-label">
        {{ 'features.data.path_filter.related_entity' | translate }}
      </div>
      @for (association of associations(); track association.edge) {
        <button
          type="button"
          role="menuitem"
          class="g-menu-item w-full"
          (click)="addAssociation(association.edge, association.quantifier)">
          <i [class]="ICONS.treeStructure" aria-hidden="true"></i>
          <span class="ellipsis flex-1 text-left">{{ association.label }}</span>
          <span class="shortcut">
            {{
              'features.data.path_filter.cardinality.' + association.cardinality | translate
            }}
          </span>
        </button>
      }
    }
  </div>
</ng-template>
